<template>
    <div class="select-tree-wrapper">
        <el-popover placement="bottom-start" width="300" trigger="click" :disabled="disabled" v-model="visible"
            popper-class="select-tree-popover" append-to-body @show="handlePopoverShow" @hide="handlePopoverHide">
            <div class="tree-content">
                <el-input v-if="filterable" v-model="filterText" size="small" placeholder="搜索节点"
                    prefix-icon="el-icon-search"
                    style="margin-bottom: 8px;width: 96%;margin-left: 2%;margin-right: 2%;"></el-input>
                <el-tree ref="tree" :data="treeData" :props="treeProps" :node-key="nodeKey"
                    :default-expand-all="defaultExpandAll" :expand-on-click-node="false"
                    :check-on-click-node="checkOnClickNode" :highlight-current="!multiple" @node-click="handleNodeClick"
                    :default-checked-keys="checkedKeys" :show-checkbox="multiple" @check="handleCheck"
                    :filter-node-method="filterNode" style="max-height: 300px; overflow-y: auto;">
                    <span class="custom-tree-node" slot-scope="{ node, data }">
                        <span>{{ node.label }}</span>
                        <span v-if="data.areaTag" class="tree-tag">{{ data.areaTag }}</span>
                    </span>
                </el-tree>
            </div>
            <el-input slot="reference" :value="displayLabel" :placeholder="placeholder" :disabled="disabled"
                :clearable="clearable" :size="size" :style="inputStyle" readonly @clear="handleClear">
                <span slot="suffix" class="suffix-wrapper">
                    <i v-if="clearable && displayLabel" class="el-input__icon el-icon-circle-close clear-icon"
                        @click.stop="handleClear">
                    </i>
                    <i class="el-input__icon el-icon-arrow-down" :class="{ 'is-reverse': visible }">
                    </i>
                </span>
            </el-input>
        </el-popover>
    </div>
</template>

<script>
export default {
    name: 'selectTree',
    props: {
        // v-model 绑定的值
        value: {
            type: [String, Number, Array],
            default: null
        },
        // 树形数据
        treeData: {
            type: Array,
            required: true,
            default: () => []
        },
        // 占位符
        placeholder: {
            type: String,
            default: '请选择'
        },
        // 是否禁用
        disabled: {
            type: Boolean,
            default: false
        },
        // 是否可清空
        clearable: {
            type: Boolean,
            default: true
        },
        // 是否可搜索
        filterable: {
            type: Boolean,
            default: false
        },
        // 尺寸
        size: {
            type: String,
            default: 'medium'
        },
        // 输入框样式
        inputStyle: {
            type: String,
            default: 'width: 100%'
        },
        // 树节点配置
        treeProps: {
            type: Object,
            default: () => ({
                children: 'children',
                label: 'areaName'
            })
        },
        // 节点唯一标识
        nodeKey: {
            type: String,
            default: 'id'
        },
        // 是否默认展开所有节点
        defaultExpandAll: {
            type: Boolean,
            default: false
        },
        // 是否多选
        multiple: {
            type: Boolean,
            default: false
        },
        // 选择模式：'first' 只能选第一级，'last' 只能选最后一级，'all' 都可以选
        selectMode: {
            type: String,
            default: 'all',
            validator: (value) => ['first', 'last', 'all'].includes(value)
        }
    },
    data() {
        return {
            visible: false,
            currentValue: null,
            displayLabel: '',
            checkedKeys: [],
            filterText: ''
        }
    },
    watch: {
        value: {
            handler(newVal) {
                this.currentValue = newVal
                this.updateDisplayLabel()
                this.updateCheckedKeys()
            },
            immediate: true
        },
        treeData: {
            handler() {
                this.updateDisplayLabel()
                this.updateCheckedKeys()
            },
            deep: true
        },
        filterText(val) {
            if (this.$refs.tree) {
                this.$refs.tree.filter(val)
            }
        }
    },
    methods: {
        // 过滤节点
        filterNode(value, data) {
            if (!value) return true
            const label = data[this.treeProps.label] || ''
            return label.indexOf(value) !== -1
        },
        // 判断节点是否可以选中
        canSelectNode(node) {
            if (this.selectMode === 'first') {
                // 只能选第一级：节点没有父节点或父节点是根节点
                return !node.parent || node.parent.data[this.nodeKey] === '00000000-0000-0000-0000-000000000000'
            } else if (this.selectMode === 'last') {
                // 只能选最后一级：节点没有子节点或所有子节点都被禁用
                return !node.childNodes || node.childNodes.length === 0 || node.childNodes.every(child => !child.data.isEnabled)
            }
            return true
        },
        // 判断节点是否为最后一级
        isLeafNode(node) {
            return !node.childNodes || node.childNodes.length === 0 || node.childNodes.every(child => !child.data.isEnabled)
        },
        // 处理节点点击
        handleNodeClick(data, node) {
            // 检查节点是否启用
            if (data.isEnabled === false) {
                return
            }

            // 检查是否符合选择模式
            if (!this.canSelectNode(node)) {
                if (this.selectMode === 'first') {
                    this.$message.warning('只能选择第一级节点')
                } else if (this.selectMode === 'last') {
                    this.$message.warning('只能选择最后一级节点')
                }
                return
            }

            if (this.multiple) {
                // 多选模式
                const nodeKey = data[this.nodeKey]
                const index = this.checkedKeys.indexOf(nodeKey)
                if (index > -1) {
                    this.$refs.tree.setChecked(nodeKey, false)
                } else {
                    this.$refs.tree.setChecked(nodeKey, true)
                }
            } else {
                // 单选模式
                this.currentValue = data[this.nodeKey]
                this.displayLabel = data[this.treeProps.label]
                this.visible = false
                this.$emit('input', this.currentValue)
                this.$emit('change', this.currentValue, data)
            }
        },
        // 处理多选时的check事件
        handleCheck(data, checked) {
            const nodeKey = data[this.nodeKey]
            const node = this.$refs.tree.getNode(nodeKey)

            // 检查节点是否启用
            if (data.isEnabled === false) {
                this.$refs.tree.setChecked(nodeKey, false)
                return
            }

            // 检查是否符合选择模式
            if (!this.canSelectNode(node)) {
                this.$refs.tree.setChecked(nodeKey, false)
                if (this.selectMode === 'first') {
                    this.$message.warning('只能选择第一级节点')
                } else if (this.selectMode === 'last') {
                    this.$message.warning('只能选择最后一级节点')
                }
                return
            }

            const checkedNodes = this.$refs.tree.getCheckedNodes(false, true)
            this.checkedKeys = checkedNodes.map(node => node[this.nodeKey])
            this.currentValue = this.checkedKeys

            // 更新显示标签
            this.displayLabel = checkedNodes.map(node => node[this.treeProps.label]).join(', ')

            this.$emit('input', this.currentValue)
            this.$emit('change', this.currentValue, checkedNodes)
        },
        // 更新显示标签
        updateDisplayLabel() {
            if (!this.currentValue) {
                this.displayLabel = ''
                return
            }

            if (this.multiple) {
                if (Array.isArray(this.currentValue) && this.currentValue.length > 0) {
                    const labels = this.findNodesByKeys(this.currentValue).map(node => node[this.treeProps.label])
                    this.displayLabel = labels.join(', ')
                } else {
                    this.displayLabel = ''
                }
            } else {
                const node = this.findNodeByKey(this.currentValue)
                this.displayLabel = node ? node[this.treeProps.label] : ''
            }
        },
        // 更新选中的keys
        updateCheckedKeys() {
            if (this.multiple && Array.isArray(this.currentValue)) {
                this.checkedKeys = this.currentValue
                this.$nextTick(() => {
                    if (this.$refs.tree) {
                        this.$refs.tree.setCheckedKeys(this.checkedKeys)
                    }
                })
            } else if (!this.multiple && this.currentValue) {
                this.checkedKeys = [this.currentValue]
                this.$nextTick(() => {
                    if (this.$refs.tree) {
                        this.$refs.tree.setCurrentKey(this.currentValue)
                    }
                })
            } else {
                this.checkedKeys = []
            }
        },
        // 根据key查找节点
        findNodeByKey(key) {
            if (!key) return null
            return this.findNodeInTree(this.treeData, key)
        },
        // 根据keys查找多个节点
        findNodesByKeys(keys) {
            if (!Array.isArray(keys) || keys.length === 0) return []
            return keys.map(key => this.findNodeByKey(key)).filter(node => node !== null)
        },
        // 在树中递归查找节点
        findNodeInTree(tree, key) {
            for (let node of tree) {
                if (node[this.nodeKey] === key) {
                    return node
                }
                if (node.children && node.children.length > 0) {
                    const found = this.findNodeInTree(node.children, key)
                    if (found) return found
                }
            }
            return null
        },
        // 处理 popover 显示
        handlePopoverShow() {
            this.filterText = ''
            this.$nextTick(() => {
                this.initTreeState()
            })
        },
        // 初始化树的状态
        initTreeState() {
            if (this.$refs.tree) {
                // 展开已选中的节点
                if (this.multiple && this.checkedKeys.length > 0) {
                    this.$refs.tree.setCheckedKeys(this.checkedKeys)
                } else if (!this.multiple && this.currentValue) {
                    const node = this.$refs.tree.getNode(this.currentValue)
                    if (node) {
                        this.$refs.tree.setCurrentKey(this.currentValue)
                    }
                }
            }
        },
        // 处理 popover 隐藏
        handlePopoverHide() {
            this.filterText = ''
            if (this.$refs.tree) {
                this.$refs.tree.filter('')
            }
        },
        // 处理清空
        handleClear() {
            this.currentValue = this.multiple ? [] : null
            this.displayLabel = ''
            this.checkedKeys = []
            if (this.$refs.tree) {
                this.$refs.tree.setCheckedKeys([])
                this.$refs.tree.setCurrentKey(null)
            }
            this.$emit('input', this.currentValue)
            this.$emit('change', this.currentValue, null)
        }
    },
    computed: {
        checkOnClickNode() {
            return this.multiple
        }
    }
}
</script>

<style lang="less" scoped>
.select-tree-wrapper {
    width: 100%;
}

.tree-content {
    padding: 4px 0;
}

.custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    padding-right: 8px;

    .tree-tag {
        color: #909399;
        font-size: 12px;
        margin-left: 8px;
    }
}

// 禁用节点的样式
::v-deep .el-tree-node.is-disabled>.el-tree-node__content {
    color: #c0c4cc;
    cursor: not-allowed;
}

// 选中节点的样式
::v-deep .el-tree-node.is-current>.el-tree-node__content {
    background-color: #f5f7fa;
}

// 下拉箭头旋转动画
::v-deep .el-input__icon.is-reverse {
    transform: rotate(180deg);
}

.suffix-wrapper {
    display: inline-flex;
    align-items: center;
}

.clear-icon {
    cursor: pointer;
    margin-right: 4px;
    color: #c0c4cc;
}

.clear-icon:hover {
    color: #909399;
}
</style>

<style lang="less">
// 全局样式，用于 popover
.select-tree-popover {
    padding: 8px 0;
}
</style>
